import type { AssetControlView, FleetCommandRow } from "@/api/client";
import { ACT_LABEL, LEVEL_LABEL, STATE_MEANS, reasonText,
         CommandStateChip as StateChip } from "@/modules/fleet/catalogue";
import { ago } from "@/lib/format";

/** Everything about ONE asset, read-only: what state it is in, who is holding
 *  it, what firmware it is mid-way through, and what it has been told.
 *
 *  ITS ONE JOB IS TO NOT LIE ABOUT STATE, because three different things look
 *  like "immobilised" and only one means the vehicle cannot move:
 *
 *    HOLD PLACED, NOT YET APPLIED  the device has the command and is waiting
 *                                  for the vehicle to halt. It is STILL MOVING.
 *                                  Rendering this as "immobilised" is the
 *                                  error an operator would act on.
 *    APPLIED                       in force on the vehicle.
 *    HOLD PLACED, NOT ACTUATABLE   no connected part installed, so the lien
 *                                  stands and nothing can reach the asset.
 *
 *  And the EFFECTIVE TYPE is shown with the level that decided it, because the
 *  highest open level dictates the type (OFL-06k): a company that asked for
 *  WHEELLOCK can be getting FREEWHEEL while a financier hold stands, and a
 *  bare "immobilised" would hide that from both of them.
 *
 *  A COMPONENT RATHER THAN A PAGE. It was `/fleet/control`, its own subpage,
 *  until Dattatray said that made no sense — once the command page can target
 *  a single asset with the same filters, a separate page for one asset is a
 *  second way to do one thing (D-111, revised). So this is the detail that
 *  appears UNDER the target selector once one asset is chosen, and it holds no
 *  controls of its own: commanding lives in one place on the page.
 */
export function AssetDetail({ v }: { v: AssetControlView }) {
  return (
    <>
      <Banner v={v} />

      <div className="section-label tight">
        Holds — every one must clear before the asset is mobile
      </div>
      <div className="tblwrap">
        <table className="tbl">
          <thead><tr>
            <th>Level</th><th>Type</th><th>Placed by</th><th>When</th><th>Note</th>
          </tr></thead>
          <tbody>
            {v.holds.length === 0 && (
              <tr><td colSpan={5} className="cap">No holds.</td></tr>
            )}
            {v.holds.map((h) => (
              <tr key={h.level}>
                <td>{LEVEL_LABEL[h.level] ?? `level ${h.level}`}</td>
                <td>{ACT_LABEL[h.action]}</td>
                <td className="mono">{h.placed_by}</td>
                <td>{h.placed_at ? `${ago(h.placed_at)} ago` : "—"}</td>
                <td>{h.note ?? "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {v.fota.length > 0 && (
        <>
          <div className="section-label tight">
            Firmware updates on this asset — read-only
          </div>
          <div className="tblwrap">
            <table className="tbl">
              <thead><tr>
                <th>Rollout</th><th>Target</th><th>Node</th><th>State</th>
                <th>Attempts</th><th>Converged</th>
              </tr></thead>
              <tbody>
                {v.fota.map((f, i) => (
                  <tr key={i}>
                    <td>{f.batch}</td>
                    <td className="num">v{f.target_version}</td>
                    <td>{f.software}</td>
                    <td>{f.state}</td>
                    <td className="num">{f.attempts}</td>
                    <td>{f.converged_at ? `${ago(f.converged_at)} ago` : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ fontSize: 11.5, color: "var(--t-mute)", marginTop: 6 }}>
            Shown here so a command's behaviour makes sense — a device
            mid-rollout may be waiting for the same halt an immobilise is.
            Rollouts are started on the FOTA page.
          </div>
        </>
      )}

      {/* ACTIVE AND ARCHIVED, SPLIT — the same shape FOTA uses for batches,
          and the reason is not cosmetic: an operator scanning for "what is
          still outstanding" must not read past a hundred finished rows, and an
          `applied` command reappearing in the active list would read as a
          command still trying to land. The server has returned both lists
          since the MVP and this component rendered neither. */}
      <div className="section-label tight">
        Active commands — still able to change
      </div>
      <CommandTable rows={v.commands_active ?? v.commands} showExpiry
        empty="Nothing outstanding." />

      <div className="section-label tight">Archived commands — finished</div>
      <CommandTable rows={v.commands_archived ?? []}
        empty="Nothing has finished yet." />
    </>
  );
}

/** One command log. `showExpiry` only on the ACTIVE table: a finished command
 *  has an expiry in the past, and a column of dead deadlines is noise that
 *  makes the live one harder to spot. */
function CommandTable({ rows, empty, showExpiry = false }: {
  rows: FleetCommandRow[]; empty: string; showExpiry?: boolean;
}) {
  return (
    <div className="tblwrap">
      <table className="tbl">
        <thead><tr>
          <th>When</th><th>Level</th><th>Command</th><th>State</th>
          <th>Means</th><th>Reason</th>
          {showExpiry && <th>Orbit retries until</th>}
          <th>By</th>
        </tr></thead>
        <tbody>
          {rows.length === 0 && (
            <tr><td colSpan={showExpiry ? 8 : 7} className="cap">{empty}</td></tr>
          )}
          {rows.map((c) => (
            <tr key={c.cmd_id}>
              <td>{c.created_at ? `${ago(c.created_at)} ago` : "—"}</td>
              <td>{c.level != null ? (LEVEL_LABEL[c.level] ?? c.level) : "—"}</td>
              <td>{ACT_LABEL[c.action] ?? c.action}</td>
              <td><StateChip c={c} /></td>
              <td style={{ fontSize: 12 }}>{STATE_MEANS[c.state] ?? "—"}</td>
              <td>{reasonText(c.reason_code)}</td>
              {showExpiry && (
                // NAMED "Orbit retries until", not "expires": the restraint
                // does NOT lift at this time. A hold lives until it is
                // released, and labelling this "expires" would tell an
                // operator a locked vehicle frees itself (OFL-06c).
                <td title="when Orbit stops retrying delivery — the hold itself does not lift">
                  {c.expires_at ? `in ${ago(c.expires_at)}` : "—"}
                </td>
              )}
              <td className="mono" style={{ fontSize: 11 }}>{c.created_by}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Banner({ v }: { v: AssetControlView }) {
  const armed = v.commands.find((c) => c.state === "received");
  if (!v.immobile) {
    return (
      <div className="panel panel-pad" style={{ borderLeft: "3px solid var(--ok)" }}>
        <b style={{ color: "var(--ok)" }}>MOBILE</b> — no holds on this asset.
      </div>
    );
  }
  // IMMOBILE per Orbit's holds. Now say which of the three it actually is.
  const applied = v.commands.some((c) => c.state === "applied");
  const colour = applied ? "var(--bad)" : "var(--warn)";
  return (
    <div className="panel panel-pad" style={{ borderLeft: `3px solid ${colour}` }}>
      <b style={{ color: colour }}>
        {applied ? "IMMOBILISED" : armed ? "ARMED — NOT YET APPLIED" : "HOLD PLACED"}
      </b>
      {" — "}
      {ACT_LABEL[v.effective_action]}
      {v.effective_level != null && (
        <> , decided by <b>{LEVEL_LABEL[v.effective_level] ?? `level ${v.effective_level}`}</b></>
      )}
      {!applied && armed && (
        <div style={{ marginTop: 6, fontSize: 12.5 }}>
          The device has the command and is waiting for the vehicle to come to a
          halt (controller data valid, speed and RPM zero, for 5 s).
          <b> The vehicle can still move until then.</b>
        </div>
      )}
      {!v.actuatable && (
        <div style={{ marginTop: 6, fontSize: 12.5, color: "var(--warn)" }}>
          No connected part is installed on this asset, so the hold is recorded
          but nothing can be sent to it.
        </div>
      )}
    </div>
  );
}
